import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ListResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalyticalDatasetResultRow } from './types';

interface UseAnalyticalDatasetResultsQueryArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** Serialized grid params (`page=…&pageSize=…&sort=…&filter=…`) from the shared DataGrid. */
  searchParams?: string;
}

/** One page of per-patient results for an analytical dataset's latest PROCESSED run. */
export const useAnalyticalDatasetResultsQuery = ({
  projectId,
  analyticalDatasetProfileId,
  searchParams,
}: UseAnalyticalDatasetResultsQueryArgs) => {
  // The shared DataGrid serializes its state with a leading '?'; re-parse to a
  // bare string so the URL can't end up as `results??page=…`, which the backend
  // reads as a '?page' key and serves page 1 forever.
  const normalizedSearchParams = searchParams ? new URLSearchParams(searchParams).toString() : '';

  return useQuery({
    queryKey: analysisKeys.datasetResults(analyticalDatasetProfileId, normalizedSearchParams),
    queryFn: () =>
      authFetch<ListResponse<AnalyticalDatasetResultRow>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}` +
          `/results${normalizedSearchParams ? `?${normalizedSearchParams}` : ''}`
      ),
    enabled: projectId.length > 0 && analyticalDatasetProfileId.length > 0,
    retry: false,
    // Keep the previous page's rows while the next page loads so the grid
    // doesn't collapse to its loading skeleton on every pagination click.
    placeholderData: keepPreviousData,
    // The cache key is not execution-scoped, so refetch on mount to reflect the
    // latest run.
    staleTime: 0,
  });
};
